<script setup>
import { ref, watch } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { useForm } from 'laravel-precognition-vue-inertia'
import { trans } from 'laravel-vue-i18n'
import axios from 'axios'
import Drawer from 'primevue/drawer'
import Button from 'primevue/button'
import FormInput from '../../../../widget/components/ui-components/FormInput.vue'
import MarkdownEditor from '../editor/MarkdownEditor.vue'

const visible = defineModel('visible', { type: Boolean, default: false })

const form = useForm('post', '/app/contacts', {
    name: '',
    email: '',
    phone: '',
    notes: '',
})

const duplicates = ref([])
let duplicatesTimer = null

watch([() => form.email, () => form.phone], () => {
    clearTimeout(duplicatesTimer)
    duplicatesTimer = setTimeout(checkDuplicates, 400)
})

async function checkDuplicates() {
    if (!form.email && !form.phone) {
        duplicates.value = []
        return
    }
    const { data } = await axios.post('/app/contacts/check-duplicates', {
        email: form.email || null,
        phone: form.phone || null,
    })
    duplicates.value = data.duplicates
}

function goToContact(id) {
    visible.value = false
    router.visit('/app/contacts/' + id)
}

function submit() {
    form.submit({
        onSuccess: () => {
            visible.value = false
            form.reset()
            duplicates.value = []
        },
    })
}

watch(visible, (val) => {
    if (!val) {
        form.reset()
        form.clearErrors()
        duplicates.value = []
    }
})
</script>

<template>
    <Drawer v-model:visible="visible" position="right" class="!w-full md:!w-[28rem]">
        <template #header>
            <span class="font-semibold text-lg">{{ $t('contacts.create_title') }}</span>
        </template>

        <div class="flex flex-col gap-4">
            <FormInput
                v-model="form.name"
                type="text"
                :label="$t('contacts.detail_full_name')"
                :invalid="form.errors.name"
            />
            <FormInput
                v-model="form.email"
                type="text"
                :label="$t('contacts.detail_email')"
                :invalid="form.errors.email"
            />
            <FormInput
                v-model="form.phone"
                type="phone"
                :label="$t('contacts.detail_phone')"
                :invalid="form.errors.phone"
            />

            <div v-if="duplicates.length" class="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 flex flex-col gap-2">
                <p class="text-sm text-amber-800 dark:text-amber-300 font-medium">
                    {{ $t('contacts.duplicate_warning') }}
                </p>
                <div v-for="dup in duplicates" :key="dup.id" class="flex items-center justify-between gap-2">
                    <span class="text-sm text-amber-900 dark:text-amber-200">{{ dup.name || dup.email || dup.phone }}</span>
                    <Button size="small" text :label="$t('contacts.duplicate_go_to')" @click="goToContact(dup.id)" />
                </div>
            </div>

            <div class="flex flex-col gap-2">
                <label class="font-medium text-sm">{{ $t('contacts.detail_notes') }}</label>
                <MarkdownEditor v-model="form.notes" :placeholder="$t('contacts.notes_placeholder')" />
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end gap-2 w-full">
                <Button severity="secondary" text :label="$t('common.cancel')" @click="visible = false" />
                <Button :label="$t('common.save')" :loading="form.processing" @click="submit" />
            </div>
        </template>
    </Drawer>
</template>
